import { useIntl } from "react-intl";
import { Avatar } from "primereact/avatar";
import { Message } from "primereact/message";
import * as GlobalHelper from "../../../helpers/GlobalHelper";
import * as Displayhelper from "../../../helpers/DisplayHelper";
import MoreActions from "../moreActions";
import intl from "../../../i18n/intl";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import OptionalTag from "../OptionalTag";

dayjs.extend(utc);
dayjs.extend(timezone);

const customIntl = intl();

export const Tags = (data) => {
  return data?.tags?.join(", ") || "-";
};

export const DateOnly = (key) => (data) => {
  return (data[key] && dayjs(data[key]).format("DD MMM YYYY")) || "-";
};

export const NameAndEmail =
  (nameKey = "name", emailKey = "email") =>
  (data) => {
    return (
      <div className="flex gap-2">
        <Avatar
          label={Displayhelper.getUserShortName(data?.[nameKey])}
          size="small"
          shape="circle"
          className="edexlms-header-profile-avatar"
        />
        <div className="flex flex-column gap-1">
          <div className="text-blue-900">{data?.[nameKey]}</div>
          <div className="text-secondary">{data?.[emailKey]}</div>
        </div>
      </div>
    );
  };

export const TypeAndContentType = (data) => {
  // Map backend type values to our constant names for i18n
  const getTypeNameForI18n = (type) => {
    switch (type?.toLowerCase()) {
      case "scorm":
        return "scorm";
      case "video":
        return "video";
      case "pdf":
        return "pdf";
      case "html":
        return "html";
      case "googleform":
      case "google_form":
        return "googleform";
      default:
        return type?.toLowerCase() || "";
    }
  };

  const typeNameForI18n = getTypeNameForI18n(data?.type);

  return (
    <div className="flex flex-column gap-2">
      <div>
        {typeNameForI18n
          ? customIntl.formatMessage({
              id: typeNameForI18n,
              defaultMessage: data?.type,
            })
          : data?.type || "-"}
      </div>
      {(data?.type === "Scorm" && data?.contentType && (
        <div className="text-muted text-sm">({data?.contentType})</div>
      )) ||
        null}
    </div>
  );
};

export const FullNameAndEmail =
  (firstNameKey = "firstName", lastNameKey = "lastName", emailKey = "email") =>
  (data) => {
    return (
      <div className="flex gap-2">
        <Avatar
          label={Displayhelper.getUserShortName(
            `${data?.[firstNameKey]}${
              data?.[lastNameKey] ? ` ${data?.[lastNameKey]}` : ""
            }`
          )}
          size="small"
          shape="circle"
          className="edexlms-header-profile-avatar"
        />
        <div className="flex flex-column gap-1">
          <div className="text-blue-900">{`${data?.[firstNameKey]}${
            data?.[lastNameKey] ? ` ${data?.[lastNameKey]}` : ""
          }`}</div>
          <div className="text-secondary">{data?.[emailKey]}</div>
        </div>
      </div>
    );
  };

export const Text =
  (key, i18n = false) =>
  (data) => {
    return i18n ? customIntl.formatMessage({ id: data[key] }) : data[key];
  };

export const NameAndLogo =
  (nameKey = "name", logoKey = "logo") =>
  (data) => {
    // A row without an image (an optional reward image or program banner)
    // shows only its name: building a URL from the empty value pointed the
    // image at ".../null", which the browser shows as broken.
    const url = data?.[logoKey]
      ? GlobalHelper.AddQueryParam(
          `${new URL(data[logoKey], process.env.REACT_APP_URL).toString()}`,
          "v",
          process.env.REACT_APP_VERSION
        )
      : null;

    return (
      <div className="flex flex-column gap-2">
        {(url && (
          <img
            src={url.updatedUrl}
            className="edexlms-enterprise-list-logo"
            alt={data?.[nameKey]}
          />
        )) ||
          null}
        <div>{data?.[nameKey]}</div>
      </div>
    );
  };

export const Status = (data) => {
  const intl = useIntl();
  const status = data?.isActive ? "active" : "inactive";
  return (
    <Message
      severity={data?.isActive ? "success" : "error"}
      text={intl.formatMessage({ id: status })}
      icon={""}
    />
  );
};

export const BooleanText = (key) => (data) => {
  return data[key] ? "Yes" : "No";
};

export const DateTime = (key) => (data) => {
  return data[key] ? dayjs(data[key]).format("DD MMM YYYY hh:mm a") : "-";
};

export const CreatedBy = (data) => {
  let name = data?.createdByUser?.firstName || "-";
  if (data?.createdByUser?.lastName) {
    name += ` ${data?.createdByUser?.lastName}`;
  }
  return name;
};

export const Permission = (accessKey) => (data) => {
  const permission = data?.scope?.[accessKey];
  return (
    <ul className="edexlms-scope-permission-list">
      <li
        className={`pi pi-eye ${permission.includes("Read") ? "active" : ""}`}
      ></li>
      <li
        className={`pi pi-plus ${
          permission.includes("Create") ? "active" : ""
        }`}
      ></li>
      <li
        className={`pi pi-pencil ${
          permission.includes("Update") ? "active" : ""
        }`}
      ></li>
      <li
        className={`pi pi-trash ${
          permission.includes("Delete") ? "active" : ""
        }`}
      ></li>
    </ul>
  );
};

export const StringData = (key) => (data) => {
  return data[key] || "-";
};

export const OtherActions = (actions, notAllowedKey, notAllowedValue) => {
  const render = (data) => {
    return (
      <MoreActions
        actions={actions}
        data={data}
        notAllowedKey={notAllowedKey}
        notAllowedValue={notAllowedValue}
      />
    );
  };
  // What the row menus show besides the row itself: which actions there are
  // and which the signed-in user may use. The shared List redraws its cells
  // when this changes (PrimeReact otherwise redraws a cell only when its row
  // changes, so a menu kept the greying of an earlier role).
  render.menuState = actions.map((action) => [
    action.label,
    Boolean(action.isAllowed),
    action.hidden?.field ?? null,
    action.hidden?.value ?? null,
  ]);
  return render;
};

export const LearningStatus = (key) => (data) => {
  const status = data?.[key]?.toLowerCase() ?? "notstarted";
  let color = "var(--orange-500)";
  if (status === "completed") {
    color = "var(--green-500)";
  } else if (status === "overdue") {
    color = "var(--red-500)";
  } else if (
    status === "inprogress" ||
    status === "started" ||
    status === "incomplete"
  ) {
    color = "#0EA5E9";
  }

  return (
    <div
      style={{
        backgroundColor: color,
        color: "white",
        width: "100px",
        textAlign: "center",
        borderRadius: "5px",
        padding: "2px",
      }}
      className="text-sm"
    >
      {customIntl.formatMessage({ id: status }).toUpperCase()}
    </div>
  );
};

export const SentStatus = (data) => {
  return (
    <Message
      severity={data?.isSent ? "success" : "error"}
      text={data?.isSent ? "Success" : "Failed"}
      icon={""}
    />
  );
};

export const TrainingType = (data) => {
  return data?.isOptional ? (
    <OptionalTag />
  ) : (
    customIntl.formatMessage({ id: "training_required" })
  );
};

// An assignment's start and end are the start of a day in the assignment's
// own time zone (the one chosen when it was added), so they are shown in
// that zone, like the day that was entered (F-BUG-9). Answers without a time
// zone, or with one this browser does not know, use the browser's zone.
const assignmentDay = (value, data) => {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zone = data?.timezone || browserZone;
  try {
    return dayjs.utc(value).tz(zone).format("DD MMM YYYY");
  } catch {
    return dayjs.utc(value).tz(browserZone).format("DD MMM YYYY");
  }
};

export const AssignmentStartDate = (data) => {
  if (data?.isDateFlexible || !data?.startDate) {
    return "-";
  }
  return assignmentDay(data.startDate, data);
};

export const AssignmentEndDate = (data) => {
  if (data?.isDateFlexible || data?.isOptional || !data?.endDate) {
    return "-";
  }
  return assignmentDay(data.endDate, data);
};

export const NameWithOptionalTag = (key) => (data) => {
  return (
    <div className="flex align-items-center gap-2">
      <span>{data?.[key]}</span>
      {(data?.isOptional && <OptionalTag />) || null}
    </div>
  );
};
